import { signInWithPopup } from "firebase/auth"
import { CloseCodes } from "../../types/enum/CloseCodes"
import { IUserMetadataJSON } from "../../types/interfaces/UserMetadata"
import type { AuthMethod } from "../../services/session-manager"
import { auth, googleProvider } from "./firebase"
import store from "./stores"
import { logIn, setProfile } from "./stores/NetworkStore"
import { LocalStoreKeys, localStore } from "./pages/utils/store"

interface AuthSession {
  uid: string
  displayName: string
  sessionToken: string
  walletAddress: string | null
  authMethod: AuthMethod
}

export async function authenticateUser(): Promise<AuthSession> {
  // Check if user is already authenticated via stored session
  const storedSession = localStore.get(LocalStoreKeys.SESSION_TOKEN)
  const networkState = store.getState().network

  if (storedSession?.sessionToken && networkState.sessionToken) {
    // Already logged in
    fetchProfile()
    return {
      uid: networkState.uid,
      displayName: networkState.displayName,
      sessionToken: networkState.sessionToken,
      walletAddress: networkState.walletAddress || null,
      authMethod: (networkState.authMethod as AuthMethod) || "phantom"
    }
  }

  // Try to restore session from storage
  if (storedSession?.sessionToken) {
    try {
      // Verify the stored session is still valid
      const response = await fetch("/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storedSession.sessionToken}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        const session: AuthSession = {
          uid: data.uid,
          displayName: data.displayName,
          sessionToken: data.sessionToken,
          walletAddress: data.walletAddress || null,
          authMethod: data.authMethod || "phantom"
        }

        // Update store and local storage
        store.dispatch(logIn(session))
        localStore.set(LocalStoreKeys.SESSION_TOKEN, {
          sessionToken: data.sessionToken,
          uid: data.uid,
          authMethod: data.authMethod
        }, 60 * 60 * 24) // 24 hours

        fetchProfile()
        return session
      }
    } catch (error) {
      // Session invalid, clear it
      localStore.delete(LocalStoreKeys.SESSION_TOKEN)
    }
  }

  // No valid session found - user needs to log in
  throw CloseCodes.USER_NOT_AUTHENTICATED
}

export async function connectWallet(): Promise<AuthSession> {
  // Check if Phantom is installed
  const phantom = (window as any).phantom?.solana
  if (!phantom?.isPhantom) {
    throw new Error("Phantom wallet not found. Please install Phantom.")
  }

  try {
    // Connect to Phantom
    const response = await phantom.connect()
    const walletAddress = response.publicKey.toString()

    // Get nonce from server
    const nonceResponse = await fetch(`/auth/nonce?walletAddress=${walletAddress}`)
    if (!nonceResponse.ok) {
      throw new Error("Failed to get authentication nonce")
    }
    const { nonce, message } = await nonceResponse.json()

    // Sign the message
    const encodedMessage = new TextEncoder().encode(message)
    const signedMessage = await phantom.signMessage(encodedMessage, "utf8")
    const signature = Buffer.from(signedMessage.signature).toString("base64")

    // Verify signature with server
    const verifyResponse = await fetch("/auth/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        walletAddress,
        signature,
        nonce
      })
    })

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json()
      throw new Error(error.error || "Authentication failed")
    }

    const data = await verifyResponse.json()
    const session: AuthSession = {
      uid: data.walletAddress,
      displayName: data.displayName,
      sessionToken: data.sessionToken,
      walletAddress: data.walletAddress,
      authMethod: "phantom"
    }

    // Store session
    store.dispatch(logIn(session))
    localStore.set(LocalStoreKeys.SESSION_TOKEN, {
      sessionToken: data.sessionToken,
      uid: data.walletAddress,
      authMethod: "phantom"
    }, 60 * 60 * 24) // 24 hours

    fetchProfile()
    return session
  } catch (error: any) {
    if (error.code === 4001) {
      throw new Error("User rejected the connection request")
    }
    throw error
  }
}

export async function connectGoogle(): Promise<AuthSession> {
  try {
    // Firebase Google sign-in popup
    const result = await signInWithPopup(auth, googleProvider)
    const idToken = await result.user.getIdToken()

    // Send to our server for verification and session creation
    const response = await fetch("/auth/google", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || "Google authentication failed")
    }

    const data = await response.json()
    const session: AuthSession = {
      uid: data.uid,
      displayName: data.displayName,
      sessionToken: data.sessionToken,
      walletAddress: data.walletAddress || null,
      authMethod: "google"
    }

    // Store session
    store.dispatch(logIn(session))
    localStore.set(LocalStoreKeys.SESSION_TOKEN, {
      sessionToken: data.sessionToken,
      uid: data.uid,
      authMethod: "google"
    }, 60 * 60 * 24) // 24 hours

    fetchProfile()
    return session
  } catch (error: any) {
    if (error.code === "auth/popup-closed-by-user") {
      throw new Error("Sign-in popup was closed")
    }
    throw error
  }
}

export async function disconnect(): Promise<void> {
  // Sign out of Firebase
  try {
    await auth.signOut()
  } catch (e) {
    // ignore - may not be a Google user
  }

  // Disconnect Phantom if connected
  const phantom = (window as any).phantom?.solana
  if (phantom?.isPhantom) {
    try {
      await phantom.disconnect()
    } catch (e) {
      // ignore
    }
  }

  localStore.delete(LocalStoreKeys.SESSION_TOKEN)
}

export function getSessionToken(): string {
  return store.getState().network.sessionToken
}

export async function fetchProfile(forceRefresh: boolean = false) {
  const profile = store.getState().network.profile
  const token = store.getState().network.sessionToken

  if (!forceRefresh && profile) {
    return Promise.resolve(profile)
  }

  if (!token) {
    return Promise.reject("No session token")
  }

  return fetch(`/profile?t=${Date.now()}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((res) => res.json())
    .then((profile: IUserMetadataJSON) => {
      store.dispatch(setProfile(profile))
    })
}
